import { defineStore } from 'pinia';

const DEFAULTS = {
  alcinos: false,
  rms: false,
  maintenance: false,
};

const REFRESH_INTERVAL = 60_000;

function evaluateStrategy(strategy, context) {
  switch (strategy.name) {
    case 'default':
      return true;
    case 'userWithId': {
      const ids = (strategy.parameters?.userIds ?? '').split(',').map((s) => s.trim());
      return context.userId ? ids.includes(context.userId) : false;
    }
    default:
      return false;
  }
}

function isFeatureEnabled(feature, context) {
  if (!feature.enabled) return false;
  if (!feature.strategies?.length) return true;
  return feature.strategies.some((s) => evaluateStrategy(s, context));
}

async function fetchFeatures() {
  const url = process.env.VUE_APP_FEATURE_FLAGS_URL;
  const instanceId = process.env.VUE_APP_FEATURE_FLAGS_INSTANCE_ID;
  const appName = process.env.VUE_APP_FEATURE_FLAGS_APP_NAME;

  const res = await fetch(`${url}/client/features?time=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'UNLEASH-INSTANCEID': instanceId,
      'UNLEASH-APPNAME': appName,
    },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const { features } = await res.json();
  return features;
}

export const useFeatureStore = defineStore('feature', {
  state: () => ({ flags: { ...DEFAULTS }, userId: null, _features: [] }),

  getters: {
    isEnabled: (state) => (name) => state.flags[name] ?? false,
  },

  actions: {
    _applyFeatures() {
      const context = { userId: this.userId };
      for (const feature of this._features) {
        if (feature.name in this.flags) {
          this.flags[feature.name] = isFeatureEnabled(feature, context);
        }
      }
    },

    async refresh() {
      try {
        this._features = await fetchFeatures();
        this._applyFeatures();
      } catch (e) {
        console.warn('[FeatureFlags] unreachable, keeping current values:', e.message);
      }
    },

    init() {
      this.refresh();
      setInterval(() => this.refresh(), REFRESH_INTERVAL);
    },

    setUser(email) {
      this.userId = email;
      this._applyFeatures();
    },
  },
});
